import { givenName } from '@contracts/assistant';
import { weekdayLabel } from '@contracts/assistant-briefing';
import type { ISODate } from '@contracts/common';
import {
  ASSIGNEE_GROUP_LABEL, ASSIGNEE_GROUPS, DELEGATION_CADENCE_LIMITS, DELEGATOR_GROUP_LABEL, DELEGATOR_GROUPS, type AssigneeGroup, type DelegationCadence,
  type DelegationListDTO, type DelegationListItemDTO, type DelegationSignalDTO, type DelegationState, type DelegatorGroup,
} from '@contracts/delegation';
import { addDays, businessDate, daysBetween, workdaysBetween } from '@domain/dates';

// AI S9 L3 (§8.1, §8.2): the "Giao việc" block's words, pure so a unit test pins them. Times are HCM business time
// (UTC+7 all year, `assistant-briefing.ts`), whatever the laptop's zone.

/** "Hỏi lại" is a comment (§7.1): the comment limit the API holds (`MAX_LONG_TEXT`, api tasks/schema.ts). */
export const QUESTION_MAX = 20_000;

/** The sentence both 409s show (§10): the server's own words differ per code. */
export const CHANGED = 'Việc vừa thay đổi, xem lại trước khi bấm';

const OFFSET_MS = 7 * 3_600_000;
const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "14/10". */
export const dayMonth = (date: ISODate): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

/** The product's weekday form for a deadline (D93): "Thứ 6, 10/10". */
export const dueDate = (date: ISODate): string => `${weekdayLabel(date)}, ${dayMonth(date)}`;

/** An ISO day as a local midnight, the form react-day-picker reads (never `new Date(iso)`, which is UTC). */
export function localDate(date: ISODate): Date {
  const d = new Date(2000, 0, 1);
  d.setFullYear(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
  return d;
}

/** A local calendar day back to ISO. */
export const isoOf = (d: Date): ISODate =>
  `${String(d.getFullYear()).padStart(4, '0')}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** How far the deadline is (§8.2): "còn 1 ngày", "tới hạn hôm nay", "quá hạn 3 ngày". */
export function dueLeft(daysLeft: number): string {
  if (daysLeft > 0) return `còn ${daysLeft} ngày`;
  if (daysLeft === 0) return 'tới hạn hôm nay';
  return `quá hạn ${-daysLeft} ngày`;
}

/** "Cập nhật cuối" and a signal's time (§8.2): "09:12" today, "hôm qua 16:20", else "Thứ 2, 05/10 08:05". */
export function whenText(instant: string, now: Date = new Date()): string {
  const local = new Date(new Date(instant).getTime() + OFFSET_MS);
  const clock = `${pad2(local.getUTCHours())}:${pad2(local.getUTCMinutes())}`;
  const day = businessDate(new Date(instant));
  const today = businessDate(now);
  if (day === today) return clock;
  if (day === addDays(today, -1)) return `hôm qua ${clock}`;
  return `${dueDate(day)} ${clock}`;
}

/** The reminder line (D83): the parts that are on, "tắt" when none is. */
export function cadenceLine(c: DelegationCadence): string {
  const parts: string[] = [];
  if (c.beforeDueDays > 0) parts.push(`trước hạn ${c.beforeDueDays} ngày`);
  if (c.blockedDays !== null) parts.push(`chặn quá ${c.blockedDays} ngày`);
  if (c.overdue) parts.push('quá hạn');
  if (c.silentDays !== null) parts.push(`im ${c.silentDays} ngày`);
  return parts.length === 0 ? 'tắt' : parts.join(' · ');
}

type Option = { value: string; label: string };
const range = (min: number, max: number): Option[] =>
  Array.from({ length: max - min + 1 }, (_, i) => ({ value: String(min + i), label: `${min + i} ngày` }));
const OFF = 'Tắt';

/** The cadence popover's choices (D83): "Tắt" first — 0 for the days before the deadline, '' (null) for the rest. */
export const CADENCE_OPTIONS: Record<'beforeDueDays' | 'blockedDays' | 'silentDays', readonly Option[]> = {
  beforeDueDays: [{ value: '0', label: OFF }, ...range(1, DELEGATION_CADENCE_LIMITS.beforeDueDays.max)],
  blockedDays: [{ value: '', label: OFF },
    ...range(DELEGATION_CADENCE_LIMITS.blockedDays.min, DELEGATION_CADENCE_LIMITS.blockedDays.max)],
  silentDays: [{ value: '', label: OFF },
    ...range(DELEGATION_CADENCE_LIMITS.silentDays.min, DELEGATION_CADENCE_LIMITS.silentDays.max)],
};

/** The block's pill (D95): the words differ per role. */
export function stateWord(role: 'delegator' | 'assignee', state: DelegationState,
  o: { delegatorName: string; requestedDeadline: ISODate | null }): string {
  switch (state) {
    case 'waiting': return role === 'assignee' ? 'Chờ bạn nhận' : 'Chờ nhận';
    case 'accepted': return 'Đã nhận';
    case 'question': return 'Đang hỏi lại';
    case 'deadline_requested':
      if (role === 'assignee') return `Chờ ${givenName(o.delegatorName)} quyết lùi hạn`;
      return o.requestedDeadline === null ? 'Xin lùi hạn' : `Xin lùi tới ${dayMonth(o.requestedDeadline)}`;
  }
}

export type PillTone = 'info' | 'ok' | 'warn';
/** Waiting is information, accepted is good, a request or a question asks for the other party. */
export function pillTone(state: DelegationState): PillTone {
  if (state === 'waiting') return 'info';
  if (state === 'accepted') return 'ok';
  return 'warn';
}

/** A signal's verb phrase after its actor's name (§8.1 "Dòng thời gian", mockup §1). */
export function signalText(s: Pick<DelegationSignalDTO, 'kind' | 'details'>, assigneeName: string): string {
  const to = s.details.to ? ` tới ${dayMonth(s.details.to)}` : '';
  switch (s.kind) {
    case 'delegated': return `giao cho ${givenName(assigneeName)}`;
    case 'accepted': return 'đã nhận';
    case 'deadline_requested': return `xin lùi hạn${to}`;
    case 'deadline_accepted': return `đồng ý lùi hạn${to}`;
    case 'deadline_declined': return 'không đồng ý lùi hạn';
    case 'deadline_withdrawn': return 'rút yêu cầu lùi hạn';
    case 'question': return 'hỏi lại';
    case 'clarified': return 'đã rõ';
    case 'progress_asked': return 'hỏi tiến độ · đã trả lời tự động';
    case 'tracking_off': return 'tắt theo dõi';
    case 'tracking_on': return 'bật theo dõi';
    case 'restored': return `khôi phục giao cho ${givenName(assigneeName)}`;
  }
}

// AI S9 L3 T11–T12 (§8.3; D90, D96, D97): the two Workplace lists.

export type ListGroup = DelegatorGroup | AssigneeGroup;
/** A group's pill: the block's three tones, plus "late" (overdue red) and "mut" (grey) as the mockup draws them. */
export type GroupTone = 'info' | 'ok' | 'warn' | 'late' | 'mut';
type GroupLook = { label: string; tone: GroupTone; count: string | null };

/** Each group's heading pill, and its word in the banner's counts (null: a calm group, not counted, mockup §5, §6). */
const DELEGATOR_LOOK: Readonly<Record<DelegatorGroup, GroupLook>> = {
  decide: { label: DELEGATOR_GROUP_LABEL.decide, tone: 'warn', count: 'cần quyết' },
  risk: { label: DELEGATOR_GROUP_LABEL.risk, tone: 'late', count: 'có rủi ro' },
  blocked: { label: DELEGATOR_GROUP_LABEL.blocked, tone: 'mut', count: 'bị chặn' },
  waiting: { label: DELEGATOR_GROUP_LABEL.waiting, tone: 'info', count: null },
  on_track: { label: DELEGATOR_GROUP_LABEL.on_track, tone: 'ok', count: null },
};
const ASSIGNEE_LOOK: Readonly<Record<AssigneeGroup, GroupLook>> = {
  waiting: { label: ASSIGNEE_GROUP_LABEL.waiting, tone: 'info', count: 'chờ nhận' },
  due_soon: { label: ASSIGNEE_GROUP_LABEL.due_soon, tone: 'warn', count: 'sắp hạn' },
  doing: { label: ASSIGNEE_GROUP_LABEL.doing, tone: 'ok', count: null },
};

export type ListSection = { group: ListGroup; label: string; tone: GroupTone; items: DelegationListItemDTO[] };

/** The list's groups in the spec's order (§8.3), empty ones left out; rows keep the server's deadline order. */
export function listSections(list: DelegationListDTO): ListSection[] {
  const order: readonly ListGroup[] = list.side === 'mine' ? DELEGATOR_GROUPS : ASSIGNEE_GROUPS;
  const looks: Readonly<Record<string, GroupLook>> = list.side === 'mine' ? DELEGATOR_LOOK : ASSIGNEE_LOOK;
  return order
    .map((group) => ({ group, label: looks[group]!.label, tone: looks[group]!.tone,
      items: list.items.filter((i) => i.group === group) }))
    .filter((section) => section.items.length > 0);
}

/**
 * The banner's counts (D90, mockup §5 "1 cần quyết · 2 có rủi ro · 1 bị chặn", §6 "1 chờ nhận · 2 sắp hạn"): the groups
 * that ask for attention, non-zero only, each the number of its rows. '' when none does.
 */
export function listSubtitle(list: DelegationListDTO): string {
  const looks: Readonly<Record<string, GroupLook>> = list.side === 'mine' ? DELEGATOR_LOOK : ASSIGNEE_LOOK;
  return listSections(list)
    .flatMap((s) => { const word = looks[s.group]!.count; return word === null ? [] : [`${s.items.length} ${word}`]; })
    .join(' · ');
}

const MINUTE = 60_000;
/** "vừa xong", "45 phút trước", "9 giờ trước", "hôm qua", "4 ngày trước", then "01/10" (HCM business days). */
export function agoText(instant: string, now: Date = new Date()): string {
  const at = new Date(instant);
  const minutes = Math.floor((now.getTime() - at.getTime()) / MINUTE);
  if (minutes < 1) return 'vừa xong';
  if (minutes < 60) return `${minutes} phút trước`;
  const days = daysBetween(businessDate(at), businessDate(now));
  if (days <= 0) return `${Math.floor(minutes / 60)} giờ trước`;
  if (days === 1) return 'hôm qua';
  if (days < 7) return `${days} ngày trước`;
  return dayMonth(businessDate(at));
}

const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** A row's status: a pill in a tone, or muted words. */
export type ListStatus = { text: string; tone: 'warn' | 'late' | 'muted' };

/** Days a card is past its deadline, by the HCM business day. */
const lateDays = (deadline: ISODate, now: Date): number => Math.max(1, daysBetween(deadline, businessDate(now)));

/**
 * A row's status words (P16). "Tôi đã giao" by group (D96: a disabled assignee reads "Người nhận đã nghỉ", spec §5.3);
 * "Nhờ tôi" by state (D95): the delegator's decision, a question, overdue, else "Đã nhận". The side decides: both
 * sides have a 'waiting' group.
 */
export function listStatus(item: DelegationListItemDTO, now: Date = new Date(), side: DelegationListDTO['side'] = 'mine'): ListStatus {
  if (side === 'for_me') {
    if (item.state === 'waiting') return { text: 'Chờ bạn nhận', tone: 'muted' };
    if (item.state === 'deadline_requested') return { text: `Chờ ${givenName(item.delegator.name)} quyết lùi hạn`, tone: 'warn' };
    if (item.state === 'question') return { text: 'Đang hỏi lại', tone: 'warn' };
    if (item.overdue) return { text: `Quá hạn ${lateDays(item.deadline, now)} ngày`, tone: 'late' };
    return { text: 'Đã nhận', tone: 'muted' };
  }
  switch (item.group) {
    case 'decide':
      return item.state === 'deadline_requested'
        ? { text: item.requestedDeadline === null ? 'Xin lùi hạn' : `Xin lùi tới ${dayMonth(item.requestedDeadline)}`, tone: 'warn' }
        : { text: 'Đang hỏi lại', tone: 'warn' };
    case 'risk':
      if (item.overdue) return { text: `Quá hạn ${lateDays(item.deadline, now)} ngày`, tone: 'late' };
      if (!item.assignee.active) return { text: 'Người nhận đã nghỉ', tone: 'late' };
      return { text: `Im ${item.silentWorkdays} ngày`, tone: 'muted' };
    case 'blocked': {
      const since = item.blockedSince === null ? 0
        : Math.max(0, workdaysBetween(businessDate(new Date(item.blockedSince)), businessDate(now)));
      return { text: `Chặn ${since} ngày`, tone: 'muted' };
    }
    case 'waiting': return { text: `Giao ${agoText(item.lastSignalAt, now)}`, tone: 'muted' };
    default: return { text: capital(agoText(item.lastSignalAt, now)), tone: 'muted' };
  }
}
